/** @module csi/task/_TaskFilmstrip */
define([
	"dojo/_base/declare",
	"underscore",
	"csi/html",
	"csi/table/FilmStrip",
	"./util"
], function(declare, _, html, FilmStrip, TaskUtil) {

	// TODO: What happens if return to view and batchGroups contains a group that no longer contains multiple tasks?

	return declare(FilmStrip, {
		allowDeselect: false,
		filmstripContentView: "ContentView",
		filmstripHeaderView: "HeaderView",

		/** @property {string[]} batchGroups - query result id's of groups where batch is enabled */
		batchGroups: null,

		/** @property {string[]} selectedTasks - tasks selected to act on; normally only one, but could be multiple if batched */
		selectedTasks: null,

		/**
		 * Return true if view has selected tasks
		 * @return {boolean}
		 */
		hasSelectedTasks: function() {
			return !!this.selectedTasks.length;
		},

		/**
		 * Return true if given group query result is batched
		 * @param {Result} [qr]
		 * return {boolean}
		 */
		isBatchGroup: function(qr) {
			return qr && _.contains(this.batchGroups, qr.id);
		},

		/**
		 * Return true if current filmstrip selection is batched group
		 * @param {Result} [qr]
		 * @param {boolean}
		 */
		isBatchGroupSelected: function() {
			return this.isBatchGroup(this.getFilmstripQueryResult());
		},

		/**
		 * Return true if current batched group include tasks all of which are mine
		 * @param {boolean}
		 */
		isAllTasksMyselfInBatchMode: function() {
			if (!this.isBatchGroupSelected()) {
				return false;
			}

			const results = this.getFilmstripQueryResult().childResults;

			return _.every(results, function(task) {
				return TaskUtil.canCompleteTask(task.getNode());
			});
		},

		/**
		 * Extension point called when a task is claimed
		 */
		onClaimTasks: function() { },

		/**
		 * Extension point called when a task is completed
		 * @param {string[]} [failedUrls] - list of selected URLs that were not completed (ran into error)
		 */
		onCompleteTasks: function(failedUrls) { },

		/**
		 * @override
		 * Define PostAction function for completing a task
		 */
		onInsertViewParams: function(defView, node, params) {
			const id = defView.getId();

			if (id === this.filmstripHeaderView) {
				params.onClaimTasks = this.onClaimTasks.bind(this);
				params.onCompleteTasks = this.onCompleteTasks.bind(this);
			}
		},

		/**
		 * Add batch group
		 * @param {string} id
		 */
		_addBatchGroup: function(id) {
			const groupRow = this.getRefNodeById(id);

			if (groupRow) {
				this.select(groupRow, true);
				this._batchGroupRow(groupRow, true);
			}

			this.set("batchGroups", this.batchGroups.concat(id));
		},

		/**
		 * Enable/disable group rendering for current batch groups
		 * @param {DOMNode} groupRow
		 * @param {boolean} enabled
		 */
		_batchGroupRow: function(groupRow, enabled) {
			// show/hide batch information for group and select batch (do now so that no delay with filmstripSelection fadeOut code)
			groupRow.firstChild.classList.toggle("csi-camunda-tasks-batch-enable", enabled);

			// show/hide child rows
			_.each(this.getRowChildren(groupRow), function(row) {
				html.setVisible(row, !enabled);
			});
		},

		/**
		 * @override
		 * Reset batch groups when refreshing view (when grouping changes)
		 */
		_resetLevels: function() {
			const batchGroups = this.batchGroups;

			this.set("batchGroups", []);

			if (_.contains(batchGroups, this.filmstripSelection)) {
				this.set("filmstripSelection", "");
			}

			this.inherited(arguments);
		},

		/**
		 * Track selected tasks; if no selection, then unbatch all groups as will be existing filmstrip mode
		 * @param {string} attr
		 * @param {string} oldValue
		 * @param {string} newValue
		 */
		_onWatchFilmstripSelection: function(attr, oldValue, newValue) {
			const selections = [];

			if (newValue) {
				const qr = this.getQueryResult(newValue);
				if (qr && !qr.isGroup()) {
					selections.push(qr.url);
				}
			} else {
				// reset batch groups when closing filmstrip (no selection)
				_.each(_.clone(this.batchGroups), function(id) {
					this._removeBatchGroup(id, true);
				}, this);
			}

			this.set("selectedTasks", selections);
			this.inherited(arguments);
		},

		/**
		 * Remove batch group
		 * @param {string} id
		 * @param {boolean} [bRefresh] - set to true when removing from code rather than from user action
		 */
		_removeBatchGroup: function(id, bRefresh) {
			const groupRow = this.getRefNodeById(id);

			this.set("batchGroups", _.without(this.batchGroups, id));

			if (groupRow) {
				this._batchGroupRow(groupRow, false);

				if (bRefresh) {
					// make sure to deselect before refreshing; otherwise, it will be selected again during refresh
					this.select(groupRow, false);
					this._refreshRow(groupRow);
				}
			}
		},

		/**
		 * @override
		 * If group row is batched, then apply extra styling
		 */
		_refreshRow(row, bUpdateReports) {
			// the base class will select the new row if previously selected, but that will not work since filmstrip
			// disables group selection (through UI). For now, undo restriction temporarily so selection works.
			this.restrictGroupSelection = false;

			const qr = this.getQueryResultByDomNode(row);
			const rowsNew = this.inherited(arguments);

			if (qr.isGroup()) {
				this._batchGroupRow(rowsNew[0], this.isBatchGroup(qr));
			}

			this.restrictGroupSelection = true;

			return rowsNew;
		},

		/**
		 * @override
		 * Apply the logic to redraw the corresponding targeted card information after updating the attribute on that node
		 */
		onFilmstripHeaderViewParams: function(qr, domNode) {
			var self = this;
			return {
				onRedraw: function() {
					const row = self.getRefNodeById(self.filmstripSelection);

					self.redrawNode(row);
				}
			}
		}
	});
});
